import { Minus, PauseIcon, PlayIcon, PlusIcon, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import Input from '../../components/Input';
import Modal from '../../components/Modal';
import Toggle from '../../components/Toggle';
import APIUrls from '../../core/ApiUrls';
import Toastify from '../../core/Toastify';
import { formatDate, MakeAxiosRequest } from '../../core/utils';

export interface CompanyProfile {
    id: string;
    user_id: string;
    linkedin_url: string;
    name: string;
    tagline: string | null;
    description: string | null;
    website: string | null;

    industry: string[] | null;

    hq_city: string | null;
    hq_country: string | null;
    hq_region: string | null;

    logo_url: string | null;
    logo_large_url: string | null;

    employee_count_current: number | null;
    employee_count_previous: number | null;
    employee_count_last_checked_at: string | null;

    employee_range_from: number | null;
    employee_range_to: number | null;

    followers_count_current: number | null;
    followers_count_previous: number | null;
    followers_count_last_checked_at: string | null;

    last_profile_hash: string | null;
    last_fetched_at: string | null;

    created_at: string;
    updated_at: string;

    status: {
        isRunning: boolean;
        isPaused: boolean;
        workflowId: string;
        runId: string;
        status: string;
    } | null;
}

const CompanyMonitoring = () => {
    const [linkedinUrls, setLinkedinUrls] = useState<string[]>(['']);
    const [companies, setCompanies] = useState<CompanyProfile[]>([]);
    const [uploadCompaniesModal, setUploadCompaniesModal] = useState<boolean>(false);
    const [deleteCompanyId, setDeleteCompanyId] = useState<string>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const csvFileInputRef = useRef<HTMLInputElement>(null);

    // Toggle states for company configuration
    const [alertJobPostings, setAlertJobPostings] = useState<boolean>(true);
    const [alertPosts, setAlertPosts] = useState<boolean>(true);
    const [alertHeadcountChange, setAlertHeadcountChange] = useState<boolean>(true);
    const [alertLeadershipChange, setAlertLeadershipChange] = useState<boolean>(true);
    const [alertFunding, setAlertFunding] = useState<boolean>(true);
    const [alertMerger, setAlertMerger] = useState<boolean>(false);
    const [alertAquisition, setAlertAquisition] = useState<boolean>(false);
    const [alertIPO, setAlertIPO] = useState<boolean>(false);

    // eslint-disable-next-line
    const linkedinUrlRegex = `^https?:\/\/(www\.)?linkedin\.com\/company\/[a-zA-Z0-9-_%]+\/?$`;

    const handleCsvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: results => {
                const data = results.data as any[];

                // Check if linkedin_url column exists
                if (!data.length || !data[0].linkedin_url) {
                    Toastify.error('CSV must have a "linkedin_url" column');
                    if (csvFileInputRef.current) csvFileInputRef.current.value = '';
                    return;
                }

                // Extract URLs and validate
                const urls = data.map(row => (row.linkedin_url || '').trim()).filter(url => url.length > 0);

                if (urls.length === 0) {
                    Toastify.error('No valid URLs found in CSV');
                    if (csvFileInputRef.current) csvFileInputRef.current.value = '';
                    return;
                }

                const invalidUrls = urls.filter(url => !url.match(linkedinUrlRegex));
                if (invalidUrls.length > 0) {
                    Toastify.error(`Found ${invalidUrls.length} invalid LinkedIn Company URLs`);
                    if (csvFileInputRef.current) csvFileInputRef.current.value = '';
                    return;
                }

                if (urls.length > 10) {
                    Toastify.error(`Maximum 10 companies allowed. Found ${urls.length} in CSV`);
                    if (csvFileInputRef.current) csvFileInputRef.current.value = '';
                    return;
                }

                setLinkedinUrls(urls);
                Toastify.success(`Loaded ${urls.length} companies from CSV`);
                if (csvFileInputRef.current) csvFileInputRef.current.value = '';
            },
            error: error => {
                Toastify.error(`Error parsing CSV: ${error.message}`);
                if (csvFileInputRef.current) csvFileInputRef.current.value = '';
            },
        });
    };

    const handleSubmit = async () => {
        const $linkedInUrls = linkedinUrls.map(it => it.trim()).filter(it => it !== '');
        const errored = $linkedInUrls.find(it => !it.match(linkedinUrlRegex));
        if (errored) {
            Toastify.error('Invalid Linkedin Company URLs');
            return;
        }
        const reqBody = {
            command: 'UPLOAD',
            linkedin_urls: $linkedInUrls,
        };
        try {
            const res = await MakeAxiosRequest('POST', APIUrls.getURL('COMPANY_LEADS'), reqBody);
            if (res?.status === 200) {
                Toastify.success('Uploaded Successfully');
                setUploadCompaniesModal(false);
                setLinkedinUrls(['']);
            }
        } catch (error) {
            console.log(error);
        } finally {
            fetchData();
        }
    };

    const handleStart = async (companyId: string) => {
        const reqBody = {
            companyId: companyId,
        };
        try {
            const res = await MakeAxiosRequest('POST', APIUrls.getURL('COMPANY_MONITOR'), reqBody);
            if (res?.status === 200) {
                Toastify.success('Company Started');
                fetchData();
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handlePause = async (companyId: string) => {
        const reqBody = {
            command: 'PAUSE',
            companyId: companyId,
        };
        try {
            const res = await MakeAxiosRequest('POST', APIUrls.getURL('COMPANY_LEADS'), reqBody);
            if (res?.status === 200) {
                Toastify.success('Company Paused');
                fetchData();
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handleDelete = async (companyId: string) => {
        if (!companyId) {
            Toastify.error('Error Deleting This Company');
            return;
        }
        const reqBody = {
            command: 'DELETE',
            companyId: companyId,
        };
        try {
            const res = await MakeAxiosRequest('POST', APIUrls.getURL('COMPANY_LEADS'), reqBody);
            if (res?.status === 200) {
                Toastify.success('Company Deleted');
                setDeleteCompanyId(undefined);
                fetchData();
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handleResume = async (companyId: string) => {
        const reqBody = {
            command: 'RESUME',
            companyId: companyId,
        };
        try {
            const res = await MakeAxiosRequest('POST', APIUrls.getURL('COMPANY_LEADS'), reqBody);
            if (res?.status === 200) {
                Toastify.success('Company Resumed');
                fetchData();
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handlePauseOrPlay = async (companyId: string) => {
        const company = companies.find(it => it.id === companyId);

        if (company?.status?.isPaused) {
            await handleResume(companyId);
            return;
        }

        if (company?.status?.workflowId) {
            await handlePause(companyId);
            return;
        }

        await handleStart(companyId);
    };

    const fetchData = async () => {
        try {
            const res = await MakeAxiosRequest('GET', APIUrls.getURL('COMPANY_LEADS'));
            if (res?.status === 200) {
                setCompanies(res?.data?.companies);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 5000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="w-full overflow-x-auto">
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h1 className="text-2xl font-bold text-gray-800">Company Monitoring</h1>
                <button onClick={() => setUploadCompaniesModal(true)} disabled={isLoading} className="text-sm bg-gradient-purple hover:hover-gradient-purple disabled:bg-gray-400 disabled:cursor-not-allowed transition text-white py-3 px-6 rounded-xl font-semibold shadow-sm whitespace-nowrap flex items-center gap-2">
                    {isLoading && <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />}
                    {isLoading ? 'Loading...' : 'Add Companies'}
                </button>
            </div>
            {isLoading && (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin h-10 w-10 border-4 border-purple-500 border-t-transparent rounded-full"></div>
                </div>
            )}
            {!isLoading &&
                (companies && companies.length > 0 ? (
                    <div className="bg-white border-collapse border-[1px] border-gray-300 rounded-xl shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-max">
                                <thead className="border-b border-gray-200 bg-white">
                                    <tr>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"> </th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Company Name</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">LinkedIn URL</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Industry</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">HQ Location</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Size</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Employees</th>
                                        <th className="px-4 py-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Created At</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider pr-6">Last Updated</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {companies.map(company => (
                                        <tr key={company.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-4">
                                                <div className="flex items-center">{company.logo_url ? <img src={company.logo_url} alt={company.name || company.id} className="h-10 w-10 rounded-full object-cover mr-3 flex-shrink-0" /> : <div className="h-10 w-10 rounded-full bg-gray-200" />}</div>
                                            </td>
                                            <td>
                                                <div className="min-w-0 pl-2">
                                                    <div className="text-sm font-medium text-gray-900 truncate">{company.name || (!company.last_fetched_at ? 'Fetching...' : 'Unknown')}</div>
                                                    {company.tagline && <div className="text-sm text-gray-500 truncate max-w-[180px]">{company.tagline}</div>}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="truncate max-w-[180px]">
                                                    <a href={company.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                                                        {company.linkedin_url}
                                                    </a>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="text-sm text-gray-900 truncate max-w-[180px]">{company.industry ? company.industry.join(', ') : (!company.last_fetched_at ? 'Fetching...' : 'N/A')}</div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="text-sm text-gray-900 truncate max-w-[180px]">
                                                    {company.hq_city || company.hq_region || company.hq_country
                                                        ? [company.hq_city, company.hq_region, company.hq_country].filter(Boolean).join(', ')
                                                        : (!company.last_fetched_at ? 'Fetching...' : 'N/A')}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="text-sm text-gray-900 truncate max-w-[180px]">{company.employee_range_from && company.employee_range_to ? `${company.employee_range_from} - ${company.employee_range_to}` : company.employee_count_current || (!company.last_fetched_at ? 'Fetching...' : 'N/A')}</div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="text-sm text-gray-900 truncate max-w-[180px]">{company.employee_count_current ? company.employee_count_current.toLocaleString() : (!company.last_fetched_at ? 'Fetching...' : 'N/A')}</div>
                                            </td>
                                            <td className="px-4 py-4 flex flex-row gap-3 text-sm">
                                                <button onClick={() => handlePauseOrPlay(company.id)} className="bg-gradient-purple hover:hover-gradient-purple transition text-white p-2 rounded-xl font-semibold shadow-sm whitespace-nowrap">
                                                    {company?.status?.isPaused ? <PlayIcon size={20} /> : company?.status?.workflowId ? <PauseIcon size={20} /> : <PlayIcon size={20} />}
                                                </button>
                                                <button onClick={() => setDeleteCompanyId(company.id)} className="bg-red-600 hover:bg-red-500 transition text-white p-2 rounded-xl font-semibold shadow-sm whitespace-nowrap">
                                                    <Trash2 size={20} />
                                                </button>
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">{formatDate(company.created_at)}</td>
                                            <td className="px-4 py-4 text-sm text-gray-500 pr-6">{formatDate(company.updated_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                        <div className="text-gray-400 mb-4">
                            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Companies Added</h3>
                        <p className="text-gray-500 mb-6">Get started by adding companies to monitor.</p>
                        <button className="bg-gradient-purple hover:hover-gradient-purple transition text-white py-2 px-6 rounded-xl font-semibold shadow-sm" onClick={() => setUploadCompaniesModal(true)}>
                            Add Companies
                        </button>
                    </div>
                ))}

            {/* UPLOAD MODAL */}
            <Modal isOpen={uploadCompaniesModal} onClose={() => setUploadCompaniesModal(false)} onConfirm={handleSubmit} showFooter={true} title="Add Companies" width="w-2/5" height="max-h-3/4 h-fit">
                <div className="flex flex-col gap-4">
                    <div className="flex gap-2">
                        <button onClick={() => csvFileInputRef.current?.click()} className="flex-1 flex items-center justify-center gap-2 py-2 px-4 border-2 border-dashed border-purple-300 rounded-xl text-purple-600 hover:bg-purple-50 transition">
                            <Upload size={18} />
                            <span className="text-sm font-medium">Upload CSV</span>
                        </button>
                        <input ref={csvFileInputRef} type="file" accept=".csv" onChange={handleCsvUpload} className="hidden" />
                    </div>
                    <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                        CSV must contain a <strong>linkedin_url</strong> column with valid LinkedIn company URLs
                    </div>
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-semibold text-gray-700">LinkedIn URLs</span>
                        <span className="text-sm text-gray-500">{linkedinUrls.length}/10 companies</span>
                    </div>
                    <div className="flex flex-col gap-2">
                        {linkedinUrls.map((it, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                                <Input
                                    isErrored={!it.match(linkedinUrlRegex)}
                                    value={it}
                                    onChange={value => {
                                        const updatedUrls = [...linkedinUrls];
                                        updatedUrls[idx] = value;
                                        setLinkedinUrls(updatedUrls);
                                    }}
                                    placeholder="Enter LinkedIn Company URL"
                                />
                                {idx === linkedinUrls.length - 1 ? (
                                    <button disabled={linkedinUrls.length >= 10} onClick={() => linkedinUrls.length < 10 && setLinkedinUrls([...linkedinUrls, ''])} className="text-purple-500 hover:text-purple-600 disabled:text-gray-300 disabled:cursor-not-allowed transition p-2">
                                        <PlusIcon />
                                    </button>
                                ) : (
                                    <button onClick={() => setLinkedinUrls(linkedinUrls.filter((_, i) => i !== idx))} className="text-purple-500 hover:text-purple-600 transition p-2">
                                        <Minus />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-gray-200 pt-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">Company Monitoring Configuration</h3>
                        <div className="flex flex-col gap-3">
                            <Toggle checked={alertJobPostings} onChange={setAlertJobPostings} label="Alert on New Job Postings" />
                            <Toggle checked={alertPosts} onChange={setAlertPosts} label="Alert on Posts" />
                            <Toggle checked={alertHeadcountChange} onChange={setAlertHeadcountChange} label="Alert on Headcount Change" />
                            <Toggle checked={alertLeadershipChange} onChange={setAlertLeadershipChange} label="Alert on Leadership Change" />
                            <Toggle checked={alertFunding} onChange={setAlertFunding} label="Alert on Funding" />
                            <Toggle checked={alertMerger} onChange={setAlertMerger} label="Alert on Merger" />
                            <Toggle checked={alertAquisition} onChange={setAlertAquisition} label="Alert on Acquisition" />
                            <Toggle checked={alertIPO} onChange={setAlertIPO} label="Alert on IPO" />
                        </div>
                    </div>
                </div>
            </Modal>
            <Modal isOpen={!!deleteCompanyId} onClose={() => setDeleteCompanyId(undefined)} onConfirm={() => handleDelete(deleteCompanyId!)} title={`Are you sure you want to delete this company?`}>
                {deleteCompanyId &&
                    (() => {
                        const company = companies.find(it => it.id === deleteCompanyId);
                        return (
                            <div className="space-y-4">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-sm text-gray-600 mb-2">
                                        <strong>Name:</strong> {company?.name || (!company?.last_fetched_at ? 'Fetching...' : 'Unknown')}
                                    </p>
                                    <p className="text-sm text-gray-600 mb-2">
                                        <div className="truncate max-w-full">
                                            <strong>LinkedIn: </strong>
                                            <a href={company?.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                                                {company?.linkedin_url}
                                            </a>
                                        </div>
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        <strong>Industry:</strong> {company?.industry ? company.industry.join(', ') : (!company?.last_fetched_at ? 'Fetching...' : 'N/A')}
                                    </p>
                                </div>
                            </div>
                        );
                    })()}
            </Modal>
        </div>
    );
};

export default CompanyMonitoring;
